import React from 'react';
import { IconButton, ListSubheader, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import HelpPopover from '../../../HelpPopover';
import { PROTECTED_FILL_COLUMN_TEMPLATE, type ListColumnWidth } from './ListRow';

export type ListHeaderColumn = {
  xs?: number;
  label: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  columnWidth?: ListColumnWidth;
};

export interface ListHeaderProps {
  columns: ListHeaderColumn[];
  addAriaLabel: string;
  addDescription?: string;
  addDescriptionId?: string;
  onAdd?: () => void;
  AddIcon: React.ElementType;
  trailingActionColumnCount?: number;
  useParentColumns?: boolean;
}

const gridColumnForColumn = (column: Pick<ListHeaderColumn, 'columnWidth' | 'xs'>): string => {
  if (column.columnWidth === 'content') return 'max-content';
  if (column.columnWidth === 'boundedContent') return 'fit-content(12rem)';
  if (column.columnWidth === 'icon') return '2.5rem';
  if (column.columnWidth === 'protectedFill') return PROTECTED_FILL_COLUMN_TEMPLATE;
  if (column.columnWidth === 'fill') return 'minmax(0, 1fr)';
  return `minmax(0, ${column.xs ?? 1}fr)`;
};

const ListHeader: React.FC<ListHeaderProps> = ({
  columns,
  addAriaLabel,
  addDescription,
  addDescriptionId,
  onAdd,
  AddIcon,
  trailingActionColumnCount = 1,
  useParentColumns = false,
}) => {
  const gridTemplateColumns = [...columns.map(gridColumnForColumn), ...Array.from({ length: trailingActionColumnCount }, () => '2.5rem')].join(' ');
  const addButton = (
    <IconButton aria-label={addAriaLabel} size="small" onClick={onAdd}>
      <AddIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Grid
      sx={{
        display: 'grid',
        gridColumn: useParentColumns ? '1 / -1' : undefined,
        gridTemplateColumns: useParentColumns ? 'subgrid' : gridTemplateColumns,
        columnGap: 1,
        alignItems: 'center',
        px: 2,
        py: 0.5,
      }}
    >
      {columns.map((c, idx) => (
        <Grid
          key={idx}
          data-column-width={c.columnWidth}
          sx={{
            minWidth: 0,
            display: 'flex',
            justifyContent: c.align === 'left' ? 'flex-start' : c.align === 'right' ? 'flex-end' : 'center',
          }}
        >
          <ListSubheader sx={{ p: 0, minWidth: 0 }}>
            <Typography
              variant="caption"
              noWrap
              sx={{ display: 'block', textAlign: c.align ?? 'center', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {c.label}
            </Typography>
          </ListSubheader>
        </Grid>
      ))}
      <Grid sx={{ gridColumn: `span ${trailingActionColumnCount}`, display: 'flex', justifyContent: 'center' }}>
        {addDescription ? (
          <HelpPopover id={addDescriptionId ?? `${addAriaLabel}-description`} text={addDescription} ariaLabel={addAriaLabel}>
            {addButton}
          </HelpPopover>
        ) : (
          addButton
        )}
      </Grid>
    </Grid>
  );
};

export default ListHeader;
